export const endpoints = {
  // Backend-owned UI-label catalog (status/enum/brand labels → Korean). Public, fetched on load by the
  // i18n remote-bundle. Versioned `/api/v1/medical` base path (cross-service symmetry: admin
  // `/api/v1/admin`, patient `/api/v1/patient-app`) — the established convention, served by medical-api's
  // I18nController.
  i18n: '/api/v1/medical/i18n',
  medicalStaff: {
    // My-profile (FEATURE_052) — reconciled to the decided `/api/v1/medical` base path + the medical-api
    // MedicalMyProfileController (the FE-proposal `/medical-staff/...` paths resolved to this at build).
    me: '/api/v1/medical/me',
    // Phone change (FEATURE_034) — request/verify/PUT per the authoritative BE SPEC (phone-keyed OTP).
    phoneChangeSendCode: '/api/v1/medical/me/phone/request',
    phoneChangeVerifyCode: '/api/v1/medical/me/phone/verify',
    phoneChange: '/api/v1/medical/me/phone',            // PUT
    passwordChange: '/api/v1/medical/me/password',      // PUT
    licenseNo: '/api/v1/medical/me/license-no',         // PUT — SHCT-991
    withdrawRequest: '/api/v1/medical/me/withdrawal-request',
    // Auth family + signup reconciled to the decided `/api/v1/medical` base path + the medical-api
    // MedicalAuthController. login/logout/refresh + signup all live (backend-mediated).
    login: '/api/v1/medical/auth/login',
    logout: '/api/v1/medical/auth/logout',
    refresh: '/api/v1/medical/auth/refresh',
    // EMR bridge ticket exchange (SHCT-992/994) — public, no refresh cookie. api-contracts/emr-bridge.md §B.1
    emrLogin: '/api/v1/medical/auth/emr/login',
    // Signup (FEATURE_004) — invitation lookup + phone-verify request/confirm + complete signup, on
    // MedicalAuthController. Phone-keyed OTP per the authoritative BE SPEC.
    invitation: (token: string) => `/api/v1/medical/auth/invitation/${encodeURIComponent(token)}`,
    signup: '/api/v1/medical/auth/signup',
    signupSendCode: '/api/v1/medical/auth/signup/phone-verify/request',
    signupVerifyCode: '/api/v1/medical/auth/signup/phone-verify/confirm',
    // Signup Terms (US-SU-05)
    signupTerms: '/api/v1/medical/auth/signup/terms',
    // Password reset (FEATURE_002) — logged-out email-OTP 3-step flow on MedicalAuthController.
    passwordResetRequest: '/api/v1/medical/auth/password-reset/request',
    passwordResetVerify: '/api/v1/medical/auth/password-reset/verify',
    passwordResetConfirm: '/api/v1/medical/auth/password-reset/confirm',
    // Terms (FEATURE_030, read-only) — reconciled to the decided `/api/v1/medical` base path + the
    // medical-api MedicalTermsController.
    terms: '/api/v1/medical/terms',
    // Detail is keyed by the admin `typeId` (Long), not a slug — the BE terms API has no slug.
    termByTypeId: (typeId: number) => `/api/v1/medical/terms/${typeId}`,
    // The logged-in staff's own terms list — every published term plus this account's `agreed` flag.
    // Bearer-required (under /me), unlike the public `terms` list; the /settings page reads this so it can
    // gate the [동의하기] opt-in on un-agreed optional terms.
    myTerms: '/api/v1/medical/me/terms',
    // Optional-term opt-in (records the logged-in staff's consent to the current published version).
    termAgree: (typeId: number) => `/api/v1/medical/terms/${typeId}/agree`,
    // Notifications (PRD 08, 공지) — FE proposal (api-contracts/notification.md). Read-only consumer of
    // institutional notices created in Admin Web; receive/display/confirm only.
    notifications: '/api/v1/medical/notifications',
    notificationRead: (id: string) =>
      `/api/v1/medical/notifications/${encodeURIComponent(id)}/read`,
    notificationsReadAll: '/api/v1/medical/notifications/read-all',
    // SHCT-652 — 조회수. Distinct from `/read`: read-state is once-per-staff, while 어드민 PRD 13 §3.1
    // counts EVERY open ("진입할 때마다 1씩 증가"), so this fires on each expand of a SYSTEM 공지.
    notificationView: (id: string) =>
      `/api/v1/medical/notifications/${encodeURIComponent(id)}/view`,
  },
  // ── Patient management + patient-scoped verticals (medical-api, full `/api/v1/medical` paths) ──
  // baseURL is the ORIGIN only (see .env.docker), so every path here carries the full prefix.
  patients: '/api/v1/medical/patients',
  patientsCounts: '/api/v1/medical/patients/counts',
  patientsCsv: '/api/v1/medical/patients/csv',
  patientByUid: (uid: string) => `/api/v1/medical/patients/${encodeURIComponent(uid)}`,
  patientRevert: (uid: string) => `/api/v1/medical/patients/${encodeURIComponent(uid)}/revert`,
  patientClose: (uid: string) => `/api/v1/medical/patients/${encodeURIComponent(uid)}/close`,
  patientSms: (uid: string) => `/api/v1/medical/patients/${encodeURIComponent(uid)}/sms`,
  // The app-install guide sent on registration — a different message from patientSms above, which
  // chases an unanswered questionnaire. GET reads how the last send ended; POST sends it again.
  patientInstallSms: (uid: string) =>
    `/api/v1/medical/patients/${encodeURIComponent(uid)}/install-sms`,
  patientInterventions: (uid: string) =>
    `/api/v1/medical/patients/${encodeURIComponent(uid)}/interventions`,
  patientInterventionById: (uid: string, noteId: string) =>
    `/api/v1/medical/patients/${encodeURIComponent(uid)}/interventions/${encodeURIComponent(noteId)}`,
  patientInterventionsCsv: (uid: string) =>
    `/api/v1/medical/patients/${encodeURIComponent(uid)}/interventions/csv`,
  patientProResponses: (uid: string) =>
    `/api/v1/medical/patients/${encodeURIComponent(uid)}/pro-responses`,
  patientProResponsesCsv: (uid: string) =>
    `/api/v1/medical/patients/${encodeURIComponent(uid)}/pro-responses/csv`,
  patientBiometrics: (uid: string) =>
    `/api/v1/medical/patients/${encodeURIComponent(uid)}/biometrics`,
  patientBiometricsCsv: (uid: string) =>
    `/api/v1/medical/patients/${encodeURIComponent(uid)}/biometrics/csv`,
  // Care-team staff selector (FEATURE_038), schedules (FEATURE_023/025/026), disease + manual masters,
  // home bulk-load (FEATURE_007), generic SMS send (FEATURE_027).
  staff: '/api/v1/medical/staff',
  schedules: '/api/v1/medical/schedules',
  scheduleById: (id: string | number) => `/api/v1/medical/schedules/${id}`,
  diseaseGroups: '/api/v1/medical/disease-groups',
  diseaseCodes: '/api/v1/medical/disease-codes',
  consultationManual: '/api/v1/medical/consultation-manual',
  consultationManualSearch: '/api/v1/medical/consultation-manual/search',
  homeToday: '/api/v1/medical/home/today',
  smsSend: '/api/v1/medical/sms/send',
  inquiries: '/api/v1/medical/medical-inquiries',
  notifications: '/api/v1/medical/notifications',
  // Organization details for the login footer. Fully public (no token): the footer renders on the login
  // screen, before a session exists. BE: PublicInstitutionController @ GET /api/v1/medical/institution
  // (permitAll). Replaces the two literals the `auth` catalog used to carry, so the footer can no longer
  // disagree with the rest of the product about who operates the service (V129 / SHCT-624).
  institution: '/api/v1/medical/institution',
};
